/* C3 — run-over-run "what changed" diff (GET /clients/{slug}/changes).
 * The backend compares two archived runs of the client and returns a
 * coverage-honest delta; `?narrative=1` adds a client-ready paragraph. */
import { request } from './client.ts';

export interface RunMeta {
  file?: string;
  brand?: string;
  generatedAt?: string;
  focus?: string;
  skippedLayers?: string[];
}

export interface NotComparable {
  layer: string;
  reason: string;
}

export interface ChangesResponse {
  comparable: boolean;
  reason?: string;
  slug?: string;
  from?: RunMeta;
  to?: RunMeta;
  assets?: {
    added: { key: string; title?: string }[];
    removed: { key: string; title?: string }[];
    changed: { key: string; title?: string; changes: Record<string, unknown> }[];
    prev: number;
    now: number;
  };
  opportunities?: { new: string[]; closed: string[]; prev: number; now: number };
  conflicts?: { new: string[]; resolved: string[]; prev: number; now: number };
  search?: {
    perTerm: {
      term: string;
      prevOwned: number;
      nowOwned: number;
      slots: number;
      prevNegative?: number;
      nowNegative?: number;
    }[];
  } | null;
  ai?: {
    prevShare?: number;
    nowShare?: number;
    shareDelta?: number;
    themesAppeared?: string[];
    themesResolved?: string[];
  } | null;
  edges?: { prevTotal: number; nowTotal: number; prevObserved: number; nowObserved: number } | null;
  knowledgePanel?: { claimedFrom?: string; claimedTo?: string; presentFrom?: boolean; presentTo?: boolean };
  wikidata?: { transitions: { property: string; from: string; to: string }[]; factsFixed: number } | null;
  notComparable?: NotComparable[];
  narrative?: string | null;
  narrativeCostUsd?: number | null;
  narrativeError?: string;
}

export interface ChangesOpts {
  from?: string;
  to?: string;
  narrative?: boolean;
}

/** GET /clients/{slug}/changes — the run-over-run delta for a client. */
export function getChanges(slug: string, opts: ChangesOpts = {}): Promise<ChangesResponse> {
  const p = new URLSearchParams();
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  if (opts.narrative) p.set('narrative', '1');
  const q = p.toString();
  return request<ChangesResponse>(`/clients/${encodeURIComponent(slug)}/changes${q ? '?' + q : ''}`);
}
